import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';

/** Why a locked entry's Reject is disabled — one copy shared by every
 *  reject surface (approvals page, approved-search dialog) so the
 *  explanation can't drift between screens. */
export const LOCKED_ENTRY_REJECT_TOOLTIP =
  'This timesheet period has locked — the entry can no longer be rejected';

/** Approve counterpart of {@link LOCKED_ENTRY_REJECT_TOOLTIP}: a locked
 *  period is closed to every approver action, so Approve is disabled too. */
export const LOCKED_ENTRY_APPROVE_TOOLTIP =
  'This timesheet period has locked — the entry can no longer be approved';

/**
 * Hoverable wrapper for a DISABLED decision trigger (Approve or Reject).
 * The shadcn Button applies `disabled:pointer-events-none`, so a `title`
 * set on a disabled button never fires — the explanation must live on an
 * enabled ancestor. Render this INSTEAD of the live control when the
 * action is lock-disabled (a reject popover must not open, an approve must
 * not fire for an ineligible row).
 *
 * Stops click propagation: the disabled button is `pointer-events-none`,
 * so a click in its area would otherwise land on this wrapper and bubble
 * to an ancestor row handler (e.g. the group-row expand/collapse toggle).
 */
export function DisabledActionReason({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: click guard only — the disabled control it wraps carries the semantics; the span adds no new interaction.
    // biome-ignore lint/a11y/useKeyWithClickEvents: pure stopPropagation guard, not a keyboard-actionable control — a key handler would be meaningless.
    <span title={title} className="inline-flex" onClick={(e) => e.stopPropagation()}>
      {children}
    </span>
  );
}

interface RejectReasonPopoverProps {
  /** The trigger element — the X icon button. Forwarded ref via asChild. */
  trigger: React.ReactNode;
  /** Pending state for the underlying reject mutation. */
  loading?: boolean;
  /**
   * Optional title — defaults to "Reject entry". Surface this for bulk
   * rejection ("Reject 3 entries") so the user understands the scope.
   */
  title?: string;
  /** Description shown above the textarea. */
  description?: string;
  /**
   * Whether a reason is required. Server-side validation requires one;
   * caller may set this to true on individual rejects too if they want
   * to enforce a paper trail. Defaults to required (matches the API).
   */
  required?: boolean;
  /** Called with the reason on Confirm. The popover closes automatically. */
  onConfirm: (reason: string) => void;
}

/**
 * Small popover that prompts for a reason before confirming a reject.
 * The API requires a non-empty reason for `decision: 'rejected'` (see
 * the reject schema); this UI mirrors that contract so the caller
 * can't accidentally submit an invalid reject and see a 400.
 *
 * Kept generic so per-row and bulk rejects both render the same affordance.
 */
export function RejectReasonPopover({
  trigger,
  loading,
  title = 'Reject entry',
  description = 'Tell the submitter what to fix before resubmitting.',
  required = true,
  onConfirm,
}: RejectReasonPopoverProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  // Reset state every time the popover closes — otherwise a partially
  // typed reason from a prior open carries over and might submit on the
  // next row's Confirm click.
  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const canConfirm = required ? reason.trim().length > 0 : true;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="space-y-2">
          <div className="space-y-1">
            <h4 className="text-sm font-semibold">{title}</h4>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          <Textarea
            autoFocus
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="text-sm"
          />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={!canConfirm || loading}
              onClick={() => {
                onConfirm(reason.trim());
                setOpen(false);
              }}
            >
              {loading ? 'Rejecting…' : 'Reject'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
